// @flow
import actionCreators from "@modules/reporting/actionCreator";
import { all, call, put, takeEvery } from "redux-saga/effects";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import type { Action } from "redux-action";
import {
  GetComplianceTrendsDefaultReports,
  GetComplianceTrendsMyReports,
  GetComplianceTrendsReportData,
  GetComplianceTrendsReportSchema,
  CreateComplianceTrendsReport,
  UpdateComplianceTrendsReport,
  DeleteComplianceTrendsReport,
  ExportComplianceTrendsReport,
  ExportComplianceTrendsReportSuccess,
} from "@modules/reporting/constants/actions";
import http from "@http";
import type {
  ComplianceReport,
  ComplianceReportFilters,
} from "@modules/reporting/reducer/trends/compliance/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

type TrendsReportsRequest = $Exact<{
  clientId: string,
}>;

export function* onGetComplianceTrendsDefaultReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<ComplianceReport> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/trends/compliance/default-reports`,
    );

    yield put(actionCreators.GetComplianceTrendsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetComplianceTrendsDefaultReportsError(e.response));
  }
}

export function* onGetComplianceTrendsMyReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<ComplianceReport> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/trends/compliance/my-saved-reports`,
    );

    yield put(actionCreators.GetComplianceTrendsMyReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetComplianceTrendsMyReportsError(e.response));
  }
}

type TrendsReportDataRequest = $Exact<{
  ...TrendsReportsRequest,
  report: ComplianceReport,
  noRedirect?: boolean,
}>;

export function* onGetComplianceTrendsReportData({
  payload,
}: Action<TrendsReportDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.report) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/compliance`,
      payload.report.filters,
    );

    yield put(
      actionCreators.GetComplianceTrendsReportDataSuccess({
        details: payload.report,
        reportData: response.data,
      }),
    );

    if (!payload.noRedirect) {
      yield put(push(route("REPORTING_TRENDS_COMPLIANCE_REPORT", { clientId: payload.clientId })));
    }
  } catch (e) {
    yield put(actionCreators.GetComplianceTrendsReportDataError(e.response));
  }
}

export function* onGetComplianceTrendsReportSchema({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("ComplianceTrendsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetComplianceTrendsReportSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetComplianceTrendsReportSchemaError(e.response));
  }
}

type CreateComplianceTrendsReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  name: string,
}>;

export function* onCreateComplianceTrendsReport({
  payload,
}: Action<CreateComplianceTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.name) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/compliance/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
      },
    );

    yield all([
      put(actionCreators.CreateComplianceTrendsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetComplianceTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateComplianceTrendsReportError(e.response));
  }
}

type UpdateComplianceTrendsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateComplianceTrendsReport({
  payload,
}: Action<UpdateComplianceTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/trends/client/${payload.clientId}/trends/compliance/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetComplianceTrendsReportData({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateComplianceTrendsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetComplianceTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateComplianceTrendsReportError(e.response));
  }
}

type DeleteComplianceTrendsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteComplianceTrendsReport({
  payload,
}: Action<DeleteComplianceTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/trends/client/${payload.clientId}/trends/compliance/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteComplianceTrendsReportSuccess()),
      put(actionCreators.GetComplianceTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteComplianceTrendsReportError(e.response));
  }
}

type ExportAbsenceReasonTrendsRequest = $Exact<{
  ...TrendsReportsRequest,
  filters: ComplianceReportFilters,
}>;

export function* onExportComplianceTrendsReport({
  payload,
}: Action<ExportAbsenceReasonTrendsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/compliance/export`,
      payload.filters,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportComplianceTrendsReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportComplianceTrendsReportError(e.response));
  }
}

export function* onExportComplianceTrendsReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "compliance-trends-report.csv");
}

export function* ComplianceSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetComplianceTrendsDefaultReports, onGetComplianceTrendsDefaultReports),
    takeEvery(GetComplianceTrendsMyReports, onGetComplianceTrendsMyReports),
    takeEvery(GetComplianceTrendsReportData, onGetComplianceTrendsReportData),
    takeEvery(GetComplianceTrendsReportSchema, onGetComplianceTrendsReportSchema),
    takeEvery(CreateComplianceTrendsReport, onCreateComplianceTrendsReport),
    takeEvery(UpdateComplianceTrendsReport, onUpdateComplianceTrendsReport),
    takeEvery(DeleteComplianceTrendsReport, onDeleteComplianceTrendsReport),
    takeEvery(ExportComplianceTrendsReport, onExportComplianceTrendsReport),
    takeEvery(ExportComplianceTrendsReportSuccess, onExportComplianceTrendsReportSuccess),
  ]);
}

export default ComplianceSaga;
